import { useEffect, useRef, useState, FormEvent } from "react";
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { Link } from "react-router-dom";
import Seo from "../../ui/components/Seo/Seo";

type Status = "checking" | "form" | "saving" | "done" | "invalid";

const ORANGE = "#EC642B";
const TEXT = "#1F2430";
const MUTED = "#6B7280";
const BORDER = "#D1D5DB";
const ERROR = "#D32F2F";

const css = `
.np-wrap { min-height: 60vh; display: flex; justify-content: center; align-items: flex-start; padding: 64px 16px; }
.np-card { width: 100%; max-width: 440px; text-align: center; color: ${TEXT}; }
.np-title { font-size: 32px; font-weight: 800; margin: 0 0 8px; }
.np-sub { font-size: 16px; color: ${MUTED}; margin: 0 0 32px; line-height: 1.5; }
.np-email {
  display: block;
  margin-top: 4px;
  font-weight: 600;
  color: ${TEXT};
  overflow-wrap: anywhere; /* ламає лише якщо пошта реально не влазить */
}
.np-label { display: block; text-align: left; font-size: 14px; font-weight: 600; margin: 0 0 8px; }
.np-field { position: relative; margin-bottom: 20px; }
.np-input { width: 100%; box-sizing: border-box; height: 52px; padding: 0 48px 0 16px; font-size: 16px; color: ${TEXT};
  border: 1px solid ${BORDER}; border-radius: 12px; outline: none; background: #fff; transition: border-color .15s, box-shadow .15s; }
.np-input:focus { border-color: ${ORANGE}; box-shadow: 0 0 0 3px rgba(236,100,43,.15); }
.np-input.err { border-color: ${ERROR}; }
.np-eye { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); width: 36px; height: 36px; border: 0;
  background: transparent; cursor: pointer; color: ${MUTED}; display: flex; align-items: center; justify-content: center; border-radius: 8px; }
.np-eye:hover { color: ${TEXT}; background: #F3F4F6; }
.np-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: 100%; height: 52px; font-size: 16px;
  font-weight: 600; color: #fff; background: ${ORANGE}; border: 0; border-radius: 12px; cursor: pointer; text-decoration: none; transition: background .15s, opacity .15s; }
.np-btn:hover { background: #D9561F; }
.np-btn:disabled { opacity: .6; cursor: not-allowed; }
.np-btn.ghost { background: transparent; color: ${ORANGE}; border: 1px solid ${ORANGE}; margin-top: 12px; }
.np-btn.ghost:hover { background: rgba(236,100,43,.08); }
.np-error { color: ${ERROR}; font-size: 14px; text-align: left; margin: -8px 0 16px; }
.np-icon { width: 64px; height: 64px; border-radius: 50%; margin: 0 auto 24px; display: flex; align-items: center; justify-content: center; }
.np-icon.ok { background: #E8F5E9; color: #2E7D32; }
.np-icon.bad { background: #FDECEA; color: ${ERROR}; }
.np-spin { width: 36px; height: 36px; margin: 0 auto 16px; border: 3px solid #E5E7EB; border-top-color: ${ORANGE}; border-radius: 50%; animation: np-rot .8s linear infinite; }
.np-spin.sm { width: 18px; height: 18px; border-width: 2px; margin: 0; border-color: rgba(255,255,255,.4); border-top-color: #fff; }
@keyframes np-rot { to { transform: rotate(360deg); } }
`;

const EyeIcon = ({ off }: { off: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
    {off && <line x1="3" y1="3" x2="21" y2="21" />}
  </svg>
);

const CheckIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function NewPasswordPage() {
  const [status, setStatus] = useState<Status>("checking");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const started = useRef(false);

  const params = new URLSearchParams(window.location.search);
  const oobCode = params.get("oobCode");
  const continueUrl = params.get("continueUrl");

  useEffect(() => {
    if (started.current) return; // захист від подвійного виклику в StrictMode
    started.current = true;

    if (params.get("mode") !== "resetPassword" || !oobCode) {
      setStatus("invalid");
      return;
    }

    verifyPasswordResetCode(getAuth(), oobCode)
      .then((mail) => {
        setEmail(mail);
        setStatus("form");
      })
      .catch((err) => {
        console.error("new-password verify error:", err.code, err.message);
        setStatus("invalid");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      setError("Пароль має містити щонайменше 6 символів.");
      return;
    }
    if (password !== confirm) {
      setError("Паролі не збігаються.");
      return;
    }
    setError("");
    setStatus("saving");
    try {
      await confirmPasswordReset(getAuth(), oobCode as string, password);
      setStatus("done");
    } catch (err: any) {
      console.error("new-password confirm error:", err.code, err.message);
      if (err.code === "auth/weak-password") {
        setError("Пароль занадто простий.");
        setStatus("form");
      } else if (err.code === "auth/expired-action-code" || err.code === "auth/invalid-action-code") {
        setStatus("invalid");
      } else {
        setError("Не вдалося змінити пароль. Спробуйте ще раз.");
        setStatus("form");
      }
    }
  };

  const safeContinue =
    continueUrl && continueUrl.startsWith(window.location.origin) ? continueUrl : "/";

  const mismatch = confirm.length > 0 && password !== confirm;

  return (
    <>
      <Seo
        title="Новий пароль"
        description="Встановлення нового паролю в Читанці"
        noindex
      />
      <div className="np-wrap">
        <style>{css}</style>
        <div className="np-card">
          {status === "checking" && (
            <>
              <div className="np-spin" />
              <p className="np-sub">Перевіряємо посилання…</p>
            </>
          )}

          {status === "invalid" && (
            <>
              <div className="np-icon bad"><CrossIcon /></div>
              <h1 className="np-title">Посилання недійсне</h1>
              <p className="np-sub">Воно застаріло або вже було використане. Запросіть скидання пароля ще раз.</p>
              <Link className="np-btn" to="/reset-password">Запросити новий лист</Link>
            </>
          )}

        {(status === "form" || status === "saving") && (
          <form onSubmit={onSubmit} noValidate>
            <h1 className="np-title">Новий пароль</h1>
            <p className="np-sub">
              Придумайте новий пароль для <span className="np-email">{email}</span>
            </p>

            <label className="np-label" htmlFor="np-pass">Новий пароль</label>
            <div className="np-field">
              <input
                id="np-pass"
                className="np-input"
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Щонайменше 6 символів"
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                className="np-eye"
                onClick={() => setShow((v) => !v)}
                aria-label={show ? "Сховати пароль" : "Показати пароль"}
              >
                <EyeIcon off={show} />
              </button>
            </div>

            <label className="np-label" htmlFor="np-confirm">Повторіть пароль</label>
            <div className="np-field">
              <input
                id="np-confirm"
                className={`np-input${mismatch ? " err" : ""}`}
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Введіть пароль ще раз"
                autoComplete="new-password"
              />
            </div>

            <div aria-live="polite">{error && <p className="np-error">{error}</p>}</div>

            <button type="submit" className="np-btn" disabled={status === "saving"}>
              {status === "saving" ? (<><span className="np-spin sm" /> Зберігаємо…</>) : "Змінити пароль"}
            </button>
          </form>
        )}

        {status === "done" && (
          <>
            <div className="np-icon ok"><CheckIcon /></div>
            <h1 className="np-title">Пароль змінено</h1>
            <p className="np-sub">Тепер можна увійти з новим паролем.</p>
            <a className="np-btn" href={safeContinue}>Продовжити</a>
          </>
        )}
      </div>
    </div>
  </>
  );
}